/**
 * Governance Canister Service
 *
 * Client for interacting with the governance canister on ICP.
 * Handles proposal submission, status queries, and voting.
 * Proposal listing and status counts are proxied through oracle-bridge.
 *
 * Story: BL-038.4 (oracle-bridge proxy wiring for proposals)
 */

import { memberAuthenticatedFetch } from '@/services/member-authenticated-fetch';
import { decodeJwtClaims } from '@hello-world-dao/auth';
import { getIdentityJwt } from './identity-jwt-store';
import { z } from 'zod';
import {
  addProposal,
  getProposal as getProposalFromStore,
  type ProposalDraftOutput,
  type ProposalScale,
  type VoteChoice,
  type VoteTally,
  type UserVote,
  type ProposalListItem,
  type ProposalFilters,
  type ProposalSort,
  type PaginatedResponse,
  type ProposalStatusCounts,
  type ProposalVersion,
  type OrgScope,
  type AreaOfNeed,
} from '@/stores';
import { formatAreasOfNeeds, isCampusRollup } from '@/stores';
import { serializeOrgScopeToQueryParams } from '@/features/proposals/orgScope';
import { getOracleBridgeUrl } from '@/utils/oracleBridge';

// Types for canister interactions
export interface SubmitProposalRequest {
  title: string;
  prompt: string;
  scale: ProposalScale;
  /** Areas of needs the proposal serves (multi-select) — proposal-vertical-taxonomy-reconcile. */
  areasOfNeeds: AreaOfNeed[];
  /** Proposal-AI draft output (tt-rm-14.3 rename of `thinkTankOutput`). */
  aiDraft: ProposalDraftOutput;
  /**
   * Submit-on-behalf-of-org selection (org-006-3). Three-state semantic:
   *   - `undefined` → field omitted (older callers — graceful degradation;
   *     canister-side default is HWDAO when org_id is None)
   *   - `null`      → explicit HWDAO (user actively selected HWDAO via the
   *     `<SubmitOnBehalfSelector />` dropdown; same canister-side effect
   *     as `undefined` but distinct dao-suite-layer observability signal)
   *   - `number`    → org-scoped submit; the canister stamps the proposal
   *     with this `org_id` and enforces vote-authz against the org's
   *     `org_associations` membership list (org-002-1/2/3 DONE)
   *
   * Sourced from `<SubmitOnBehalfSelector />` (org-006-3) → stashed on
   * `ProposalDraft.orgId` → threaded into this request by
   * `SubmitConfirmation.handleSubmit`.
   */
  orgId?: number | null;
}

export interface SubmitProposalResponse {
  success: boolean;
  proposalId?: string;
  error?: {
    code: string;
    message: string;
  };
}

export interface ProposalStatus {
  id: string;
  status: 'pending' | 'active' | 'passed' | 'rejected' | 'expired' | 'withdrawn';
  votesFor: number;
  votesAgainst: number;
  quorumReached: boolean;
  votingEnds: number;
}

// Timeout configuration
const CANISTER_CALL_TIMEOUT_MS = 15000; // 15 seconds
const PROPOSAL_LIST_TIMEOUT_MS = 5000; // 5 seconds for proposal listing (Story 9-1-3)
const PAGE_SIZE = 20; // Items per page for pagination

// Zod schema for ProposalListItem validation (Story 9-1-3)
// BL-189: versionNumber + hasVersions optional — added for PLATFORM-004.1
// edit history. Existing proposals without history remain valid.
// org-006-1: orgId nullable + optional — three-state semantic (undefined =
// wire field absent from older oracle-bridge; null = HWDAO-wide; number = Org-scoped).
const ProposalListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  proposer: z.string(),
  status: z.enum(['Active', 'Passed', 'Failed', 'Pending']),
  votesFor: z.number(),
  votesAgainst: z.number(),
  votesAbstain: z.number(),
  votingEndsAt: z.number(),
  createdAt: z.number(),
  versionNumber: z.number().min(1).optional(), // 1-indexed; 0 is not a valid version number
  hasVersions: z.boolean().optional(),
  orgId: z.number().int().nullable().optional(),
});

const ProposalStatusCountsSchema = z.object({
  Active: z.number(),
  Passed: z.number(),
  Failed: z.number(),
  Pending: z.number(),
});

const PaginatedProposalResponseSchema = z.object({
  items: z.array(ProposalListItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  statusCounts: ProposalStatusCountsSchema.optional(),
});

// Custom error types
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class SchemaValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SchemaValidationError';
  }
}

/**
 * Map an oracle-bridge proposal object to a ProposalListItem.
 * BL-038.6: oracle-bridge now returns `mapped_status` field.
 * BL-189: surface the PLATFORM-004.1 edit-history fields (version_number,
 * versions) if oracle-bridge includes them. Both remain optional so older
 * proposals / older oracle-bridge versions still decode cleanly.
 * BL-227: extract _ns nanosecond timestamp strings when present (BL-222
 * dual-field timestamps). Used for ordering; display stays on ms fields.
 */
function mapOracleBridgeToProposalListItem(
  proposal: Record<string, unknown>
): ProposalListItem {
  // BL-189: oracle-bridge may emit version_number as a number, or as the
  // Candid-shaped opt array `[n]` / `[]` — accept either.
  const rawVersionNumber = proposal.version_number ?? proposal.versionNumber;
  let versionNumber: number | undefined;
  if (typeof rawVersionNumber === 'number') {
    versionNumber = rawVersionNumber;
  } else if (Array.isArray(rawVersionNumber) && rawVersionNumber.length === 1 && typeof rawVersionNumber[0] === 'number') {
    versionNumber = rawVersionNumber[0];
  }

  // BL-189: hasVersions is either explicit on the wire, or inferred from
  // version_number > 1 (a version number of 1 means "original, never edited").
  const rawHasVersions = proposal.has_versions ?? proposal.hasVersions;
  const hasVersions =
    typeof rawHasVersions === 'boolean'
      ? rawHasVersions
      : typeof versionNumber === 'number'
        ? versionNumber > 1
        : undefined;

  // BL-227: extract nanosecond timestamp strings for sub-ms ordering.
  // Accept snake_case (wire) and camelCase variants.
  const createdAtNs = parseNsField(proposal.created_at_ns ?? proposal.createdAtNs);
  const votingStartAtNs = parseNsField(proposal.voting_start_at_ns ?? proposal.votingStartAtNs);
  const votingEndsAtNs = parseNsField(
    proposal.voting_end_at_ns ?? proposal.votingEndAtNs ??
    proposal.voting_ends_at_ns ?? proposal.votingEndsAtNs
  );

  // org-006-1: three-state mapping from oracle-bridge wire to ProposalListItem.orgId.
  //   - field absent       → undefined (older oracle-bridge build; no badge rendered)
  //   - field present null → null (HWDAO-wide; render "HWDAO" badge)
  //   - field present num  → number (Org-scoped; render "Org {id}" badge)
  // Defensive: an unexpected wire shape (string, object, non-finite number)
  // collapses to undefined so the UI degrades to "no badge" rather than crashing.
  const orgIdRaw = 'org_id' in proposal ? proposal.org_id : ('orgId' in proposal ? proposal.orgId : undefined);
  let orgId: number | null | undefined;
  if (orgIdRaw === undefined) {
    orgId = undefined;
  } else if (orgIdRaw === null) {
    orgId = null;
  } else if (typeof orgIdRaw === 'number' && Number.isFinite(orgIdRaw)) {
    orgId = orgIdRaw;
  } else {
    orgId = undefined;
  }

  return {
    id: String(proposal.id),
    title: String(proposal.title || ''),
    proposer: String(proposal.proposer || ''),
    status: (proposal.mapped_status as ProposalListItem['status']) || 'Pending',
    votesFor: Number(proposal.yes_votes || 0),
    votesAgainst: Number(proposal.no_votes || 0),
    votesAbstain: Number(proposal.abstain_votes || 0),
    votingEndsAt: new Date(String(proposal.voting_end_at || '')).getTime() || 0,
    createdAt: new Date(String(proposal.created_at || '')).getTime() || 0,
    ...(versionNumber !== undefined ? { versionNumber } : {}),
    ...(hasVersions !== undefined ? { hasVersions } : {}),
    ...(createdAtNs !== undefined ? { createdAtNs } : {}),
    ...(votingStartAtNs !== undefined ? { votingStartAtNs } : {}),
    ...(votingEndsAtNs !== undefined ? { votingEndsAtNs } : {}),
    ...(orgId !== undefined ? { orgId } : {}),
  };
}

/**
 * BL-227: Parse a raw nanosecond timestamp value from the wire.
 *
 * Oracle-bridge emits these as 19-digit strings (BL-222 dual-field
 * timestamps). Returns the string as-is when it looks like a valid
 * non-negative integer; returns undefined for null/undefined/invalid
 * values so callers can safely fall back to ms fields.
 */
function parseNsField(raw: unknown): string | undefined {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const s = String(raw);
  // Accept only digit strings (no sign, no decimal — nanosecond epochs are
  // plain positive integers that don't fit in a JS number).
  if (/^\d+$/.test(s)) {
    return s;
  }
  return undefined;
}

/**
 * BL-227: Compare two ProposalListItem values by creation time using ns
 * precision when available, with ms-field fallback.
 *
 * Returns negative when a < b, zero when equal, positive when a > b.
 * Suitable for use with Array.sort() — pass a comparator that calls this.
 */
export function compareProposalsByCreatedAt(
  a: Pick<ProposalListItem, 'createdAt' | 'createdAtNs'>,
  b: Pick<ProposalListItem, 'createdAt' | 'createdAtNs'>
): number {
  // Both have ns fields: use BigInt comparison for full precision.
  if (a.createdAtNs !== undefined && b.createdAtNs !== undefined) {
    const aNs = BigInt(a.createdAtNs);
    const bNs = BigInt(b.createdAtNs);
    if (aNs < bNs) return -1;
    if (aNs > bNs) return 1;
    return 0;
  }
  // Fallback to ms field.
  return a.createdAt - b.createdAt;
}

// Canister configuration
const GOVERNANCE_CANISTER_ID = import.meta.env.VITE_GOVERNANCE_CANISTER_ID || '';

/**
 * Check if we're in a mock/development environment
 */
function isMockMode(): boolean {
  return !GOVERNANCE_CANISTER_ID;
}

/**
 * Create structured log entry
 */
function log(level: 'info' | 'warn' | 'error', message: string, data?: Record<string, unknown>) {
  const entry = {
    timestamp: new Date().toISOString(),
    service: 'GovernanceCanister',
    level,
    message,
    ...data,
  };

  if (level === 'error') {
    console.error(JSON.stringify(entry));
  } else if (level === 'warn') {
    console.warn(JSON.stringify(entry));
  } else {
    console.log(JSON.stringify(entry));
  }
}

/**
 * Mock submission for development
 */
async function mockSubmitProposal(request: SubmitProposalRequest): Promise<SubmitProposalResponse> {
  log('info', 'Mock proposal submission', { title: request.title });

  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1500));

  // Simulate occasional failures for testing (disabled for now to test flow)
  // if (Math.random() < 0.1) {
  //   return {
  //     success: false,
  //     error: {
  //       code: 'MOCK_ERROR',
  //       message: 'Simulated submission failure for testing',
  //     },
  //   };
  // }

  const proposalId = `prop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Store the proposal in the mock store
  addProposal({
    id: proposalId,
    title: request.title,
    prompt: request.prompt,
    scale: request.scale,
    areasOfNeeds: request.areasOfNeeds,
    aiDraft: request.aiDraft,
  });

  return {
    success: true,
    proposalId,
  };
}

/**
 * Governance category stamped on every proposal submitted through the dao-suite
 * member wizard.
 *
 * The wizard captures the member's *areas of needs* (`Housing`/`Food & Water`/
 * `Energy`/…, multi-select) and a *scale* (`small`/`medium`/`large`). The
 * governance canister requires a `ProposalCategory`, which is a different axis
 * entirely: it selects the quorum percentage, the approval percentage, and
 * whether an automated execution dispatch fires. There is no defensible
 * areas-of-needs→category mapping, so a single conservative constant is used and
 * the selected areas are preserved in the description instead (see
 * `buildProposalDescription`).
 *
 * Why `Operational`:
 *   1. It falls in the `_ =>` arm of the canister's `get_configured_thresholds`,
 *      i.e. STANDARD quorum/approval — the baseline bar, not an elevated one.
 *   2. The ADR-034 execution dispatch fires only for Treasury `Payout` and
 *      Personnel `StatusChange`, so a member proposal in this category can
 *      never trigger an automated on-chain effect.
 *   3. `Constitutional` / `Immutable` / `Tier1Constitutional` carry 90 %
 *      thresholds, 180-day windows and CLT ratification. Assigning one of those
 *      from a domain dropdown would be a governance-integrity error.
 *
 * §for-Coby ratification: this is a consequential-design default on a
 * vote-integrity-adjacent surface. It lives here as one named constant so a
 * different ratified answer is a one-line change. Whether the wizard should
 * gain a real member-facing category picker is a product decision and is
 * deliberately NOT made here.
 */
export const PROPOSAL_CATEGORY = 'Operational';

/**
 * Voting window requested for every proposal submitted through the dao-suite
 * member wizard, in hours.
 *
 * 168 h = 7 days = the canister's `MAX_VOTING_HOURS`, and it matches the
 * commitment the confirmation screen already makes to the member ("a 7-day
 * voting period begins"). `SubmitConfirmation` renders that sentence from this
 * constant so the copy and the request cannot drift apart.
 *
 * The wizard's `scale` is deliberately NOT mapped onto this value: the member
 * picks `scale` to steer the AI draft, and silently repurposing it to set a
 * vote duration would change governance behaviour through a field with a
 * different declared meaning.
 *
 * §for-Coby ratification, same as `PROPOSAL_CATEGORY`.
 */
export const PROPOSAL_VOTING_PERIOD_HOURS = 168;

/** Server-side bound on `title` (oracle-bridge zod `.max(200)` + canister). */
export const MAX_PROPOSAL_TITLE_LENGTH = 200;

/**
 * Compose the on-chain description from the member-edited draft.
 *
 * Note (D1): the raw AI draft object stays client-side — only text the member
 * has seen and can edit is written on-chain. `scale` and the selected
 * `areasOfNeeds` are appended because the canister has no field for either;
 * without this they would be silently discarded at the wire boundary. When ≥2
 * areas are selected the trailer also carries the derived hub/campus rollup
 * indication (proposal-vertical-taxonomy-reconcile AC4).
 */
function buildProposalDescription(request: SubmitProposalRequest): string {
  const { aiDraft } = request;
  const parts: string[] = [];

  if (aiDraft?.summary) {
    parts.push(aiDraft.summary);
  }
  if (aiDraft?.body) {
    parts.push(aiDraft.body);
  }
  for (const section of aiDraft?.sections ?? []) {
    parts.push(`## ${section.heading}\n\n${section.content}`);
  }

  // The scale/areas-of-needs trailer is METADATA, not substance, so it is
  // appended only once there is real member-authored content to attach it to.
  // Appending it unconditionally made the caller's "description is required"
  // guard unreachable (the trailer alone is a non-empty string), which let a
  // draft with an empty `aiDraft` submit a proposal whose entire on-chain
  // description was the trailer. A substanceless proposal is votable and counts
  // toward quorum, so the guard has to be able to fire.
  const substance = parts.filter((p) => p && p.trim().length > 0).join('\n\n');
  if (substance.length === 0) {
    return '';
  }

  const areasLabel = formatAreasOfNeeds(request.areasOfNeeds);
  // ≥2 areas ⇒ EMERGENT hub/campus rollup — surfaced in the trailer (AC4). The
  // exact rollup word is a Menley copy pin; "campus" is provisional.
  const rollupSuffix = isCampusRollup(request.areasOfNeeds) ? ' — campus' : '';
  // A resumed legacy draft can migrate to an EMPTY selection (the retired
  // Community/Infrastructure/Other verticals map to `[]`), and the multi-select
  // default is also `[]`. Omit the whole areas-of-needs segment when nothing is
  // selected so the immutable on-chain description never carries a dangling
  // "Areas of needs: " label (code-review M1). Single-select could not hit this.
  const areasSegment = areasLabel ? ` · Areas of needs: ${areasLabel}${rollupSuffix}` : '';

  return `${substance}\n\n---\n\nScale: ${request.scale}${areasSegment}`;
}

/**
 * Map an oracle-bridge error response to a member-facing failure.
 *
 * The body is read EXACTLY ONCE (a `Response` stream cannot be re-read — see
 * bl-1153, where a second `.json()` silently rejected and a hardcoded string
 * shipped to members). `message` is preferred over `error` because the
 * oracle-bridge envelope puts the machine code in `error` and the human
 * sentence in `message`; on the unmapped canister-error path `error` carries
 * prose and is used as-is.
 */
async function submitProposalFailure(response: Response): Promise<SubmitProposalResponse> {
  const status = response.status;

  let body: Record<string, unknown> | null = null;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // Non-JSON body (HTML gateway page, empty stream, truncated response).
    // Fall through to the per-status default message — never rethrow.
    body = null;
  }

  const serverMessage =
    typeof body?.message === 'string' && body.message.trim().length > 0
      ? body.message
      : typeof body?.error === 'string' && body.error.trim().length > 0
        ? body.error
        : undefined;

  // `II_NOT_LINKED` is a distinct, actionable case: the member has a session
  // but no linked Internet Identity, so no principal can be attributed.
  if (body?.code === 'II_NOT_LINKED') {
    return {
      success: false,
      error: {
        code: 'II_NOT_LINKED',
        message:
          serverMessage ?? 'Link Internet Identity to submit a governance proposal.',
      },
    };
  }

  let code = 'SERVER_ERROR';
  let fallback = 'Failed to submit proposal. Please try again.';
  if (status === 400 || status === 422) {
    code = 'VALIDATION_ERROR';
    fallback = 'The proposal was rejected. Please review it and try again.';
  } else if (status === 401 || status === 403) {
    code = 'UNAUTHORIZED';
    fallback = 'You are not currently eligible to submit a governance proposal.';
  } else if (status === 404) {
    code = 'NOT_FOUND';
    fallback = 'The governance service could not be reached.';
  } else if (status === 429) {
    code = 'RATE_LIMITED';
    fallback = 'Too many requests. Please wait a moment and try again.';
  }

  return { success: false, error: { code, message: serverMessage ?? fallback } };
}

/**
 * Submit a proposal to the governance canister.
 *
 * Routed through oracle-bridge per ADR-014 (records on-chain, orchestration
 * off-chain): `POST /api/governance/proposals`. The browser deliberately does
 * NOT call the canister directly — `governance.create_proposal` is gated on the
 * oracle-bridge principal, and the acting member's principal is resolved
 * server-side from the session, never sent in the body (a body-supplied
 * principal would be a one-member-one-vote attribution hole).
 *
 * A success is returned ONLY when the server responded 2xx AND supplied a
 * numeric proposal id. There is no path on which a locally-generated value
 * satisfies the success condition — bl-1088 existed precisely because there
 * was one.
 */
export async function submitProposal(
  request: SubmitProposalRequest
): Promise<SubmitProposalResponse> {
  log('info', 'Submitting proposal', {
    title: request.title,
    scale: request.scale,
    // org-006-3: log the on-behalf-of selection (undefined / null = HWDAO;
    // number = org-scoped). null and undefined both collapse to canister-
    // side HWDAO default; the distinction is preserved for observability.
    orgId: request.orgId ?? null,
  });

  // Use mock mode in development (no governance canister ID configured).
  if (isMockMode()) {
    return mockSubmitProposal(request);
  }

  // bl-1088 / bl-1088-2: org-scoped submit FAILS CLOSED.
  //
  // The canister's `CreateProposalArgs` has `org_id : opt nat64`, but
  // oracle-bridge's `createGovernanceProposalSchema` and its vendored IDL do
  // not carry the field, and zod strips unknown keys. Forwarding `orgId` would
  // therefore produce an HWDAO-wide proposal while the member believes they
  // submitted on behalf of their Org — the same fabricated-outcome defect this
  // story removes, relocated one field over. Refuse instead, and do not spend a
  // request on it.
  if (typeof request.orgId === 'number') {
    log('warn', 'Org-scoped proposal submission refused (unsupported end-to-end)', {
      orgId: request.orgId,
    });
    return {
      success: false,
      error: {
        code: 'ORG_SCOPE_UNSUPPORTED',
        message:
          'Submitting on behalf of an organization is not available yet. ' +
          'Select "Hello World DAO" to submit this proposal.',
      },
    };
  }

  const title = request.title?.trim() ?? '';
  if (title.length === 0) {
    return {
      success: false,
      error: { code: 'VALIDATION_ERROR', message: 'A proposal title is required.' },
    };
  }
  // Advisory only, for the same reason as the description check below: this
  // counts CODE POINTS while oracle-bridge's zod `.max(200)` counts UTF-16 code
  // units, so a title of emoji/surrogate pairs can clear this guard and still be
  // rejected server-side. The server is authoritative and its message surfaces.
  if (Array.from(title).length > MAX_PROPOSAL_TITLE_LENGTH) {
    return {
      success: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: `Title exceeds maximum length of ${MAX_PROPOSAL_TITLE_LENGTH} characters.`,
      },
    };
  }

  const description = buildProposalDescription(request);
  if (description.length === 0) {
    return {
      success: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: 'A proposal description is required. Add content to the draft and try again.',
      },
    };
  }
  // Advisory only. oracle-bridge additionally enforces a 10,000-BYTE cap, which
  // is stricter than this code-point check for multi-byte text — so the server
  // can still reject a description that clears this guard, and its message must
  // reach the member (see `submitProposalFailure`).
  if (Array.from(description).length > MAX_PROPOSAL_DESCRIPTION_LENGTH) {
    return {
      success: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: `Description exceeds maximum length of ${MAX_PROPOSAL_DESCRIPTION_LENGTH} characters.`,
      },
    };
  }

  try {
    const baseUrl = getOracleBridgeUrl();
    const url = `${baseUrl}/api/governance/proposals`;

    const response = await withTimeout(
      memberAuthenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: PROPOSAL_CATEGORY,
          title,
          description,
          votingPeriodHours: PROPOSAL_VOTING_PERIOD_HOURS,
        }),
      }),
      CANISTER_CALL_TIMEOUT_MS
    );

    if (!response.ok) {
      return submitProposalFailure(response);
    }

    // Read the body EXACTLY ONCE, and treat a parse failure as "accepted but
    // unconfirmed" rather than letting it throw into the outer catch. A 2xx
    // whose body is lost (HTML gateway page, truncated stream, broken gzip)
    // means the write very probably LANDED — reporting it as a network error
    // told the member "please try again" and invited a duplicate proposal.
    let data: { proposalId?: unknown } | null = null;
    let bodyUnparseable = false;
    try {
      data = (await response.json()) as { proposalId?: unknown } | null;
    } catch {
      bodyUnparseable = true;
    }

    const rawId = data?.proposalId;
    // Accept ONLY a canonical non-negative integer id. `0` is a legitimate id
    // and must pass (it is stringified before the truthiness gate below, so a
    // falsy-but-valid id is not mistaken for a missing one). A numeric-ish
    // string that is not a plain integer — `'0x1A'`, `'1e3'`, `'427.5'`, `'-5'`
    // — is NOT an id oracle-bridge can emit (it sends `Number(result.Ok)`) and
    // must not become the member-visible id or the `/proposals/:id` target.
    // Digit strings are kept as strings so a large id cannot lose precision.
    const proposalId =
      typeof rawId === 'number' && Number.isInteger(rawId) && rawId >= 0
        ? String(rawId)
        : typeof rawId === 'string' && /^\d+$/.test(rawId.trim())
          ? rawId.trim()
          : undefined;

    // A 2xx without a usable id is a FAILURE, not a success with a guessed id.
    // The member must not be told a proposal exists when we cannot name it.
    if (!proposalId) {
      log('error', 'Proposal submission accepted but returned no usable proposal id', {
        status: response.status,
        bodyUnparseable,
      });
      return {
        success: false,
        error: {
          code: 'SERVER_ERROR',
          message:
            'The governance service accepted the proposal but did not confirm an ID. ' +
            'Check the proposals list before resubmitting.',
        },
      };
    }

    log('info', 'Proposal submitted', { proposalId });
    return { success: true, proposalId };
  } catch (error) {
    const isTimeout = error instanceof Error && error.message === 'Request timeout';
    log('error', 'Proposal submission failed', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: isTimeout
          ? 'The submission request timed out. Check the proposals list before resubmitting.'
          : 'Network error while submitting the proposal. Please try again.',
      },
    };
  }
}

// =============================================================================
// bl-1088-1 — shared wire helpers for the governance vote surfaces.
//
// All four member-facing vote functions below route through oracle-bridge per
// ADR-014. Direct-to-canister is FORECLOSED, not merely discouraged:
// `governance::cast_vote` opens with `require_oracle_bridge(msg_caller())`, so a
// browser-signed Internet Identity call fails the gate no matter how it is
// written. `burnService.ts`'s `HttpAgent` + `Actor.createActor` pattern is NOT a
// precedent here — `icrc1_burn` is not service-principal-gated; `cast_vote` is.
// =============================================================================

/** The vote breakdown as `GET /api/governance/proposals/:id/breakdown` sends it. */
interface VoteBreakdownWire {
  yes: number;
  no: number;
  abstain: number;
  needs_refinement: number;
  total_votes: number;
  quorum_threshold: number;
  approval_threshold: number;
  approval_percentage: number;
  quorum_met: boolean;
  approval_met: boolean;
  user_has_voted?: boolean;
  user_vote_choice?: string | null;
}

/** The proposal as `GET /api/governance/proposals/:id` sends it. */
interface ProposalWire {
  status?: unknown;
  yes_votes?: unknown;
  no_votes?: unknown;
  abstain_votes?: unknown;
  needs_refinement_votes?: unknown;
  quorum_threshold?: unknown;
  voting_end_at_ns?: unknown;
}

/** The ballot as `GET /api/governance/proposals/:id/voted/:userId` sends it. */
interface VotedWire {
  hasVoted?: unknown;
  vote?: {
    choice?: unknown;
    votedAt?: unknown;
    votedAtNs?: unknown;
  } | null;
}

/** The wire vocabulary of `castGovernanceVoteSchema`. */
type WireVoteChoice = 'Yes' | 'No' | 'Abstain' | 'NeedsRefinement';

/**
 * Explicit, total wire → client map.
 *
 * bl-1172: `NeedsRefinement` now maps to the real `needs_refinement` client
 * choice — the `VoteChoice` union grew its fourth member, so this table is total
 * over `WireVoteChoice` with no `null` fallback. Collapsing NeedsRefinement to
 * `abstain` was never an option: the canister counts it toward quorum but NOT
 * toward approval, so the two are not interchangeable.
 */
const WIRE_CHOICE_TO_VOTE: Record<WireVoteChoice, VoteChoice> = {
  Yes: 'yes',
  No: 'no',
  Abstain: 'abstain',
  NeedsRefinement: 'needs_refinement',
};

/**
 * Explicit, total canister-status → client-status map.
 *
 * The two vocabularies are NOT in a superset relation. `Executed` and `Failed`
 * have no faithful client value — `passed` / `rejected` are the least-wrong
 * approximations and the lossiness is recorded here rather than discovered in a
 * component. `expired` and `withdrawn` are client-only and the server never
 * emits them.
 *
 * An UNRECOGNISED status is deliberately absent from this table: `getProposalStatus`
 * returns `null` for it. Defaulting an unknown status to `'active'` is the
 * pre-bl-1088-1 behaviour and the exact reason a closed proposal was rendered
 * with a live vote button.
 *
 * Backed by a null-prototype object, and read through `lookupTotal` below.
 * A plain object literal is NOT a total map: `map['constructor']`,
 * `map['toString']` and `map['__proto__']` all resolve to inherited members, so
 * a `if (!mapped)` guard lets them through and the "explicit and total, fails
 * visible" property is silently false for that class of input.
 */
const CANISTER_STATUS_TO_CLIENT: Record<string, ProposalStatus['status']> = Object.assign(
  Object.create(null) as Record<string, ProposalStatus['status']>,
  {
    Draft: 'pending',
    Active: 'active',
    Approved: 'passed',
    Rejected: 'rejected',
    // bl-1433: the governance canister's ProposalStatus is 8 cases; `Executing`
    // and `Invalid` were added (ADR-034 / §14.5) and MUST have an explicit
    // target here. Absent, `lookupTotal` returns undefined and `getProposalStatus`
    // fails visible → returns null → the proposal DISAPPEARS from the member's
    // view. That silent drop is the member-facing half of bl-1433.
    Executing: 'passed', // lossy + DELIBERATE: the proposal PASSED the vote and its
    //                      orchestrated effect is in flight (ADR-034). It is NOT
    //                      done, but it is NOT `'active'` either — mapping it to
    //                      `'active'` would re-open the vote button on a proposal
    //                      whose voting has closed (the exact bl-1088-1 defect, and
    //                      the vote gate keys solely on `status === 'active'`). So
    //                      it lands in the closed/passed bucket: visible, no vote
    //                      button, honest about the vote outcome.
    Executed: 'passed', // lossy: the proposal passed AND its execution ran
    Failed: 'rejected', // lossy: the proposal did not pass, or execution failed
    Invalid: 'rejected', // §14.5 retroactive invalidation (BL-450): the void/
    //                       rejected-equivalent — no longer a live or passed proposal.
  } as const
);

/**
 * Read a lookup table by an UNTRUSTED key.
 *
 * Only own enumerable keys resolve; anything inherited from `Object.prototype`
 * (or absent) yields `undefined` so the caller's fail-visible branch runs. The
 * null-prototype tables above make this belt-and-braces, but the helper is what
 * keeps the property true if a table is ever rewritten as a plain literal.
 */
function lookupTotal<T>(table: Record<string, T>, key: unknown): T | undefined {
  if (typeof key !== 'string') return undefined;
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
}

/**
 * Read a `Response` body EXACTLY once.
 *
 * bl-1153 shipped a hardcoded string to members because a body was read twice:
 * the second `.json()` on a consumed `Response` rejects, the surrounding `catch`
 * swallowed it, and a placeholder initializer reached the UI. Every caller below
 * takes the body from here and from nowhere else.
 */
async function readJsonOnce<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    // Non-JSON body (HTML gateway page, empty stream, truncated response).
    // Never rethrow — the caller decides what an unreadable body means.
    return null;
  }
}

/** A finite JS number, or null. No coercion, no zero default. */
function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Convert a nanoseconds-since-epoch decimal STRING to milliseconds.
 *
 * The `_ns` fields cross the wire as strings precisely because they exceed
 * `Number.MAX_SAFE_INTEGER`; `parseInt` into a float loses precision. Divide as
 * a BigInt first, then narrow — the millisecond result is comfortably safe.
 */
function nsStringToMs(value: unknown): number | null {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
  try {
    return Number(BigInt(value) / BigInt(1_000_000));
  } catch {
    return null;
  }
}

/**
 * The member's own IC principal.
 *
 * In `identity-jwt` mode (dac-001-7, this suite's mode) the principal lives in
 * the identity-service JWT's `ic_principal` claim. bl-1442-e removed the legacy
 * cookie-session (`getSessionState().icPrincipal`) fallback: dao-suite never ran
 * the cookie `checkSession` on the live path, so that fallback was never
 * populated and only masked the JWT source. The JWT claim is now authoritative.
 * Returns null when the member has not linked Internet Identity — in which case
 * they cannot have an on-chain ballot.
 */
function getOwnIcPrincipal(): string | null {
  const jwt = getIdentityJwt();
  if (jwt) {
    const claims = decodeJwtClaims(jwt);
    const fromClaims = claims?.ic_principal;
    if (typeof fromClaims === 'string' && fromClaims.length > 0) return fromClaims;
  }
  return null;
}

/**
 * A stable per-member key for MOCK-MODE (`isMockMode()`) vote bookkeeping.
 *
 * bl-1442-e: replaces the removed `getSessionState().userId` read. In
 * `identity-jwt` mode the member subject is the JWT `sub` claim (the canonical
 * user identifier); `ic_principal` is a secondary stable key. Falls back to the
 * literal `'anonymous'` so an unauthenticated mock session still keys
 * deterministically — matching the prior `|| 'anonymous'` behavior. Mock mode is
 * local-dev only; real votes are keyed on-chain by principal.
 */
function getOwnUserKey(): string {
  const jwt = getIdentityJwt();
  if (jwt) {
    const claims = decodeJwtClaims(jwt);
    const sub = claims?.sub;
    if (typeof sub === 'string' && sub.length > 0) return sub;
    const principal = claims?.ic_principal;
    if (typeof principal === 'string' && principal.length > 0) return principal;
  }
  return 'anonymous';
}

/**
 * Get proposal status by ID.
 *
 * bl-1088-1: reads the real proposal from oracle-bridge. Before this story BOTH
 * branches returned a hardcoded `status: 'active'` with `votingEnds` of
 * `now + 7 days`, so the portal offered a live vote button on every proposal
 * including closed, rejected and executed ones.
 *
 * Returns `null` — not a plausible-looking proposal — whenever the server value
 * cannot be read faithfully. Callers already treat `null` as "unknown"
 * (`ProposalDetailPage`, `NotificationBell`, `NotificationsPage` all guard with
 * `status?.status !== 'active'`).
 */
export async function getProposalStatus(proposalId: string): Promise<ProposalStatus | null> {
  log('info', 'Fetching proposal status', { proposalId });

  if (isMockMode()) {
    // Mock response - return active status to enable voting in demo mode
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      id: proposalId,
      status: 'active',
      votesFor: 0,
      votesAgainst: 0,
      quorumReached: false,
      votingEnds: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days
    };
  }

  try {
    const url = `${getOracleBridgeUrl()}/api/governance/proposals/${encodeURIComponent(proposalId)}`;
    const response = await withTimeout(
      memberAuthenticatedFetch(url, { headers: { Accept: 'application/json' } }),
      CANISTER_CALL_TIMEOUT_MS
    );

    if (!response.ok) {
      log('error', 'Failed to fetch proposal status', { proposalId, status: response.status });
      return null;
    }

    const body = await readJsonOnce<ProposalWire>(response);
    if (!body) {
      log('error', 'Proposal status body was unreadable', { proposalId });
      return null;
    }

    const rawStatus = typeof body.status === 'string' ? body.status : '';
    const status = lookupTotal(CANISTER_STATUS_TO_CLIENT, rawStatus);
    if (!status) {
      // Fail VISIBLE. An unknown status resolving to `'active'` is what put a
      // live vote button on a closed proposal.
      log('error', 'Unrecognised proposal status from the governance service', {
        proposalId,
        rawStatus,
      });
      return null;
    }

    const votesFor = finiteNumber(body.yes_votes);
    const votesAgainst = finiteNumber(body.no_votes);
    const abstain = finiteNumber(body.abstain_votes);
    // `needs_refinement` ballots count toward QUORUM (governance/src/tally.rs),
    // so omitting them here would under-report the total and show a proposal
    // that has reached quorum as short of it.
    const needsRefinement = finiteNumber(body.needs_refinement_votes);
    const quorumThreshold = finiteNumber(body.quorum_threshold);
    const votingEnds = nsStringToMs(body.voting_end_at_ns);

    if (
      votesFor === null ||
      votesAgainst === null ||
      abstain === null ||
      needsRefinement === null ||
      quorumThreshold === null ||
      votingEnds === null
    ) {
      log('error', 'Proposal status response was missing required fields', { proposalId });
      return null;
    }

    return {
      id: proposalId,
      status,
      votesFor,
      votesAgainst,
      quorumReached: votesFor + votesAgainst + abstain + needsRefinement >= quorumThreshold,
      votingEnds,
    };
  } catch (error) {
    log('error', 'Failed to fetch proposal status', {
      proposalId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
}

// Mock vote storage (in-memory for development)
const mockUserVotes: Record<string, Record<string, UserVote>> = {};

/**
 * Helper to create timeout promise
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    ),
  ]);
}

/**
 * Validate the SERVER's vote-breakdown payload.
 *
 * bl-1088-1: the predecessor of this function validated the object
 * `getVoteTally` had just constructed itself, so it could never fail. This one
 * validates the response, which is the only thing worth validating.
 *
 * Every field is REQUIRED. An older oracle-bridge that omits `needs_refinement`
 * or `approval_met` must fail here rather than default to `0` / `false` — a
 * defaulted tally is a plausible-looking lie, which is the defect class this
 * story exists to remove.
 */
function isValidVoteBreakdown(data: unknown): data is VoteBreakdownWire {
  if (!data || typeof data !== 'object') return false;
  const b = data as Record<string, unknown>;
  const numbers = [
    'yes',
    'no',
    'abstain',
    'needs_refinement',
    'total_votes',
    'quorum_threshold',
    'approval_threshold',
    'approval_percentage',
  ] as const;
  for (const field of numbers) {
    if (finiteNumber(b[field]) === null) return false;
  }
  return typeof b.quorum_met === 'boolean' && typeof b.approval_met === 'boolean';
}

/**
 * Get user's vote on a proposal
 *
 * @param proposalId - ID of the proposal
 * @returns Promise resolving to the user's vote or null if not voted
 */
export async function getUserVote(proposalId: string): Promise<UserVote | null> {
  log('info', 'Fetching user vote', { proposalId });

  if (isMockMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const userId = getOwnUserKey();
    const vote = mockUserVotes[userId]?.[proposalId] ?? null;
    return vote;
  }

  // AC-6 — why this reads `/proposals/:id/voted/:principal` and NOT the
  // per-user fields on `/breakdown`, which would otherwise be one request fewer:
  //
  //   `gpBreakdownKey` (oracle-bridge/src/utils/read-cache-keys.ts) skips the
  //   30 s read cache only when the `access_token` COOKIE is present. dao-suite
  //   runs in `authMode: 'identity-jwt'` (App.tsx) — oracle-bridge no longer
  //   issues DAO sessions and the JWT is the sole gate — so its requests carry a
  //   bearer JWT and NO `access_token` cookie. `cacheRead` also runs BEFORE
  //   `optionalAuth`, so it cannot see the bearer. The breakdown response is
  //   therefore cached under a SHARED per-proposal key WITH its per-user fields:
  //   a voter's `user_has_voted:true` can be served to a member who has not
  //   voted, and vice-versa. Reading a ballot from there would be a
  //   silently-wrong not-voted (or falsely-voted) state.
  //
  //   `/voted/:principal` is uncached, is the canister's own `has_voted` +
  //   `get_vote`, and carries a genuine on-chain `votedAt`. dao-suite only ever
  //   names its OWN principal here.
  //
  // The oracle-bridge cache-key gap is filed as a follow-up; it is not fixed in
  // this diff (scope holds inside dao-suite).
  const principal = getOwnIcPrincipal();
  if (!principal) {
    // No linked Internet Identity ⇒ no on-chain ballot can exist. Do not spend a
    // request, and do not guess.
    log('info', 'No linked IC principal — cannot resolve a ballot', { proposalId });
    return null;
  }

  try {
    const url =
      `${getOracleBridgeUrl()}/api/governance/proposals/${encodeURIComponent(proposalId)}` +
      `/voted/${encodeURIComponent(principal)}`;

    const response = await withTimeout(
      memberAuthenticatedFetch(url, { headers: { Accept: 'application/json' } }),
      CANISTER_CALL_TIMEOUT_MS
    );

    if (!response.ok) {
      log('error', 'Failed to fetch user vote', { proposalId, status: response.status });
      return null;
    }

    const body = await readJsonOnce<VotedWire>(response);
    if (body?.hasVoted !== true || !body.vote) return null;

    const rawChoice = body.vote.choice;
    const wireChoice =
      rawChoice === 'Yes' || rawChoice === 'No' || rawChoice === 'Abstain' ||
      rawChoice === 'NeedsRefinement'
        ? (rawChoice as WireVoteChoice)
        : null;

    if (!wireChoice) {
      log('error', 'Unrecognised vote choice from the governance service', { proposalId });
      return null;
    }

    // Every wire choice — `NeedsRefinement` included (bl-1172) — now maps to a
    // real client `VoteChoice`; the table is total over `WireVoteChoice`, so
    // there is no unrepresentable ballot left to drop.
    const vote = WIRE_CHOICE_TO_VOTE[wireChoice];

    // The canister's own recorded time, at full precision. Never a local clock.
    const votedAt = nsStringToMs(body.vote.votedAtNs);
    if (votedAt === null) {
      log('error', 'Ballot carried no readable timestamp', { proposalId });
      return null;
    }

    // `transactionId` stays undefined — governance votes have no receipt.
    return { proposalId, vote, votedAt };
  } catch (error) {
    log('error', 'Failed to fetch user vote', {
      proposalId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
}

/**
 * Get vote tally for a proposal
 *
 * @param proposalId - ID of the proposal
 * @returns Promise resolving to the vote tally
 */
export async function getVoteTally(proposalId: string): Promise<VoteTally> {
  log('info', 'Fetching vote tally', { proposalId });

  if (isMockMode()) {
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Get proposal from store to calculate tally
    const proposal = getProposalFromStore(proposalId);
    const votesFor = proposal?.votesFor ?? 0;
    const votesAgainst = proposal?.votesAgainst ?? 0;
    const abstain = 0; // Mock: no abstain tracking in current proposal store

    const tally: VoteTally = {
      yes: votesFor,
      no: votesAgainst,
      abstain,
      needsRefinement: 0,
      totalVotes: votesFor + votesAgainst + abstain,
      // bl-1088-1: mock mode has NO canister to derive thresholds from, so it
      // reports none. The old `10` / `51` placeholders rendered a fabricated
      // quorum bar that was visually indistinguishable from a real one; zero is
      // visibly not a threshold.
      quorumRequired: 0,
      quorumMet: false,
      passingThreshold: 0,
      approvalPercentage: 0,
      approvalMet: false,
      lastUpdated: Date.now(),
    };

    return tally;
  }

  const url =
    `${getOracleBridgeUrl()}/api/governance/proposals/${encodeURIComponent(proposalId)}/breakdown`;

  // No try/catch swallow. A tally that cannot be read is an ERROR, and
  // `useVoteTallyPolling` already renders one (and backs off). Returning a
  // zeroed tally with plausible-looking thresholds — the previous behaviour —
  // is the same defect class as the mock: a fabricated governance number that
  // no surface disagrees with.
  const response = await withTimeout(
    memberAuthenticatedFetch(url, { headers: { Accept: 'application/json' } }),
    CANISTER_CALL_TIMEOUT_MS
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch the vote tally (HTTP ${response.status})`);
  }

  const body = await readJsonOnce<unknown>(response);
  if (!isValidVoteBreakdown(body)) {
    throw new Error('The governance service returned an unreadable vote tally.');
  }

  // Straight through. No arithmetic: `quorum_met`, `approval_met` and
  // `approval_percentage` are canister-computed, and recomputing a governance
  // rule client-side is a second implementation that will eventually drift.
  return {
    yes: body.yes,
    no: body.no,
    abstain: body.abstain,
    needsRefinement: body.needs_refinement,
    totalVotes: body.total_votes,
    quorumRequired: body.quorum_threshold,
    quorumMet: body.quorum_met,
    passingThreshold: body.approval_threshold,
    approvalPercentage: body.approval_percentage,
    approvalMet: body.approval_met,
    lastUpdated: Date.now(),
  };
}

/**
 * Verify vote status (used after reconnection, and to confirm a fresh cast).
 *
 * bl-1088-1: this is the fifth vote surface, and it became real by delegation —
 * `getUserVote` now reads the canister's own ballot record through oracle-bridge
 * rather than a module-level in-memory map. It is genuinely cache-free: the
 * `/voted/:principal` route carries no `cacheRead` middleware, so the original
 * "in production, this would bypass any caching" comment is now true rather than
 * aspirational.
 *
 * @param proposalId - ID of the proposal
 * @returns Promise resolving to the user's vote or null
 */
export async function verifyVoteStatus(proposalId: string): Promise<UserVote | null> {
  log('info', 'Verifying vote status', { proposalId });

  return getUserVote(proposalId);
}

/**
 * Helper to create AbortController with timeout
 */
function createTimeoutController(timeoutMs: number): {
  controller: AbortController;
  timeoutId: NodeJS.Timeout;
} {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { controller, timeoutId };
}

/**
 * Execute promise with AbortController timeout
 */
async function withAbortTimeout<T>(
  promiseFn: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number
): Promise<T> {
  const { controller, timeoutId } = createTimeoutController(timeoutMs);

  try {
    const result = await promiseFn(controller.signal);
    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new TimeoutError(`Request timed out after ${timeoutMs}ms`);
    }
    throw error;
  }
}

/**
 * Get paginated list of proposals with filters and sorting
 *
 * Story: BL-038.4 — fetches real proposals from oracle-bridge proxy.
 * Story: org-006-1 — adds the optional `orgScope` 4th positional parameter
 * that maps to the canister-side `OrgScope` filter (HWDAO-wide vs Org-scoped
 * vs All). Undefined `orgScope` ≡ `{ All: null }` ≡ no HTTP query param
 * (wire-back-compatible with the pre-org-002-3 oracle-bridge).
 *
 * In mock mode (no VITE_GOVERNANCE_CANISTER_ID), returns empty results.
 *
 * @param filters - Filter criteria (status, search, myProposals, notVoted)
 * @param sort - Sort option (newest, oldest, mostVotes, endingSoon)
 * @param page - Page number (1-indexed)
 * @param orgScope - org-006-1: optional org-scope filter (undefined = All)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Promise resolving to paginated proposal list
 */
export async function getProposals(
  filters: ProposalFilters,
  sort: ProposalSort,
  page: number,
  orgScope?: OrgScope,
  signal?: AbortSignal
): Promise<PaginatedResponse<ProposalListItem>> {
  log('info', 'Fetching proposals', { filters, sort, page });

  if (isMockMode()) {
    // No canister ID configured — return empty results
    return { items: [], total: 0, page, pageSize: PAGE_SIZE };
  }

  const fetchProposals = async (
    abortSignal: AbortSignal
  ): Promise<PaginatedResponse<ProposalListItem>> => {
    const baseUrl = getOracleBridgeUrl();
    const params = new URLSearchParams();

    // BL-038.6: Server-side pagination, filtering, sorting
    params.set('page', String(page));
    params.set('page_size', String(PAGE_SIZE));
    params.set('sort', sort);

    // Status filter (send first status if set — oracle-bridge accepts single value)
    if (filters.status.length > 0) {
      params.set('status', filters.status[0]);
    }

    // Search filter
    if (filters.search && filters.search.length >= 2) {
      params.set('search', filters.search);
    }

    // "My Proposals" filter — send proposer principal.
    // bl-1442-e: sourced from the identity-jwt claim (`getOwnIcPrincipal`), not the
    // removed cookie `getSessionState().icPrincipal`, which never populated in
    // identity-jwt mode — so this filter previously always fell through to the
    // "no principal" empty-result branch (a latent "My Proposals returns nothing"
    // bug the repoint fixes).
    if (filters.myProposals) {
      const icPrincipal = getOwnIcPrincipal();
      if (icPrincipal) {
        params.set('proposer', icPrincipal);
      } else {
        // No IC principal linked — return empty result instead of silently fetching all
        return {
          items: [],
          total: 0,
          page,
          pageSize: PAGE_SIZE,
          statusCounts: { Active: 0, Passed: 0, Failed: 0, Pending: 0 },
        };
      }
    }

    // org-006-1: optional org-scope filter. `undefined` and `{ All: null }`
    // both omit the query params (semantic identity preserved with the
    // pre-org-002-3 wire contract).
    const orgScopeParams = serializeOrgScopeToQueryParams(orgScope);
    if (orgScopeParams) {
      for (const [k, v] of Object.entries(orgScopeParams)) {
        params.set(k, v);
      }
    }

    // TODO BL-038.5: wire notVoted filter to canister
    // filters.notVoted is silently ignored — requires per-user vote lookup

    const url = `${baseUrl}/api/governance/proposals?${params.toString()}`;

    const response = await memberAuthenticatedFetch(url, { signal: abortSignal });
    if (!response.ok) {
      throw new Error(`Governance API error: ${response.status}`);
    }

    const data = await response.json();
    const rawProposals: Record<string, unknown>[] = data.proposals || [];
    const items = rawProposals.map(mapOracleBridgeToProposalListItem);

    // Parse status_counts from response (BL-038.6)
    const statusCounts: ProposalStatusCounts = data.status_counts || {
      Active: 0,
      Passed: 0,
      Failed: 0,
      Pending: 0,
    };

    const result: PaginatedResponse<ProposalListItem> = {
      items,
      total: data.total ?? items.length,
      page: data.page ?? page,
      pageSize: data.page_size ?? PAGE_SIZE,
      statusCounts,
    };

    // Validate response schema
    const parseResult = PaginatedProposalResponseSchema.safeParse(result);
    if (!parseResult.success) {
      throw new SchemaValidationError(`Invalid response schema: ${parseResult.error.message}`);
    }

    return result;
  };

  try {
    if (signal) {
      // Use provided signal
      return await fetchProposals(signal);
    } else {
      // Create our own timeout
      return await withAbortTimeout(fetchProposals, PROPOSAL_LIST_TIMEOUT_MS);
    }
  } catch (error) {
    // Re-throw abort errors so callers (useProposalList) can detect cancellation
    // and avoid wiping cached proposal state with an empty result.
    // Note: DOMException (from fetch abort) may not be instanceof Error in all envs,
    // so check .name directly instead of using `error instanceof Error` guard alone.
    const errorName = error instanceof Error ? error.name : (error as { name?: string })?.name;
    if (errorName === 'AbortError') {
      throw error;
    }
    if (error instanceof TimeoutError || error instanceof SchemaValidationError) {
      throw error;
    }
    log('error', 'Failed to fetch proposals', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    // Return empty result on error (graceful degradation)
    return {
      items: [],
      total: 0,
      page,
      pageSize: PAGE_SIZE,
    };
  }
}

// BL-038.6: getTotalProposalCount and getProposalCountsByStatus removed.
// Total count and status counts are now returned directly in the getProposals() response
// from oracle-bridge's enhanced endpoint (via `total` and `status_counts` fields).

// =============================================================================
// BL-220 / PLATFORM-004.1: Proposal detail + edit history + update
// =============================================================================

/**
 * Canister-level statuses as returned by oracle-bridge `GET /api/governance/proposals/:id`.
 * These mirror the governance.did variant names, not the mapped dao-suite status.
 */
// bl-1433: MUST mirror governance.did's 8-case ProposalStatus. `Executing`
// (ADR-034) and `Invalid` (§14.5 / BL-450) were added on the canister; omitting
// them here made `toCanisterStatus` coerce a real Executing/Invalid proposal
// down to `'Draft'` in the detail view — a silent member-facing mislabel.
export type CanisterProposalStatus =
  | 'Draft'
  | 'Active'
  | 'Approved'
  | 'Rejected'
  | 'Executing'
  | 'Executed'
  | 'Failed'
  | 'Invalid';

/**
 * BL-220: Single-proposal detail returned by oracle-bridge.
 *
 * The wire format comes from `transformProposal()` in
 * oracle-bridge/src/routes/governance-proposals.ts. Fields needed for the
 * dao-suite edit-history + update UI are surfaced here; the rest are left
 * open so future consumers can extend without churning the type.
 *
 * BL-227: `_ns` timestamp strings added for sub-ms ordering precision.
 * Display formatting still uses the ms fields; ordering uses ns when present.
 */
export interface ProposalDetail {
  id: string;
  title: string;
  description: string;
  proposer: string;
  status: CanisterProposalStatus;
  createdAt: number;      // ms since epoch
  votingStartAt: number;  // ms since epoch
  votingEndAt: number;    // ms since epoch
  versionNumber?: number;
  hasVersions: boolean;
  // BL-227: nanosecond-precision timestamps (optional; absent on older oracle-bridge).
  /** Nanoseconds-since-epoch string for creation time (BL-227). */
  createdAtNs?: string;
  /** Nanoseconds-since-epoch string for voting start time (BL-227). */
  votingStartAtNs?: string;
  /** Nanoseconds-since-epoch string for voting end time (BL-227). */
  votingEndAtNs?: string;
  /** Nanoseconds-since-epoch string for last update time (BL-227). */
  updatedAtNs?: string;
  /**
   * org-006-1 / org-006-4: Org scope identifier for this proposal.
   *
   * Three-state semantic (matches `ProposalListItem.orgId`):
   *   - `undefined` → wire field absent (older oracle-bridge) — graceful
   *                   degradation, treated as HWDAO-wide by consumers.
   *   - `null`      → HWDAO-wide (canister `Proposal.org_id` was `None`).
   *   - `number`    → Org-scoped (canister `Proposal.org_id` was `Some(id)`),
   *                   identifies the Official Org that scopes this proposal.
   *
   * Consumed by the proposal scope badge / Org filter (`ProposalCard`,
   * `OrgFilter`) to distinguish an Org-scoped proposal from an HWDAO-wide
   * one. (The org-006-4 VotingPanel disabled-state consumer was retired with
   * the direct-cast client in bl-1457-1.)
   */
  orgId?: number | null;
}

/**
 * BL-220: Maximum description length (must stay in sync with canister
 * MAX_DESCRIPTION_LENGTH). Measured in unicode code points (chars().count()),
 * not JavaScript UTF-16 code units — use Array.from(string).length for
 * validation.
 */
export const MAX_PROPOSAL_DESCRIPTION_LENGTH = 10_000;

function toCanisterStatus(raw: unknown): CanisterProposalStatus {
  if (
    raw === 'Draft' ||
    raw === 'Active' ||
    raw === 'Approved' ||
    raw === 'Rejected' ||
    raw === 'Executing' || // bl-1433
    raw === 'Executed' ||
    raw === 'Failed' ||
    raw === 'Invalid' // bl-1433
  ) {
    return raw;
  }
  return 'Draft';
}

function parseIsoToMs(iso: unknown): number {
  const t = new Date(String(iso ?? '')).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * BL-220: Fetch a single proposal by ID from oracle-bridge.
 *
 * Backing route: `GET /api/governance/proposals/:id` (oracle-bridge).
 * Returns `null` in mock mode or when the proposal cannot be loaded.
 */
export async function getProposalDetail(proposalId: string): Promise<ProposalDetail | null> {
  log('info', 'Fetching proposal detail', { proposalId });

  if (isMockMode()) {
    return null;
  }

  try {
    const baseUrl = getOracleBridgeUrl();
    const url = `${baseUrl}/api/governance/proposals/${encodeURIComponent(proposalId)}`;
    const response = await memberAuthenticatedFetch(url);
    if (!response.ok) {
      log('warn', 'Proposal detail fetch non-OK', { proposalId, status: response.status });
      return null;
    }

    const data = await response.json();
    const raw = (data?.proposal ?? data) as Record<string, unknown>;
    const rawVersionNumber = raw.version_number ?? raw.versionNumber;
    let versionNumber: number | undefined;
    if (typeof rawVersionNumber === 'number') {
      versionNumber = rawVersionNumber;
    } else if (
      Array.isArray(rawVersionNumber) &&
      rawVersionNumber.length === 1 &&
      typeof rawVersionNumber[0] === 'number'
    ) {
      versionNumber = rawVersionNumber[0];
    }

    const rawHasVersions = raw.has_versions ?? raw.hasVersions;
    const hasVersions =
      typeof rawHasVersions === 'boolean'
        ? rawHasVersions
        : typeof versionNumber === 'number'
          ? versionNumber > 1
          : false;

    // BL-227: extract nanosecond timestamp strings for ordering precision.
    const createdAtNs = parseNsField(raw.created_at_ns ?? raw.createdAtNs);
    const votingStartAtNs = parseNsField(raw.voting_start_at_ns ?? raw.votingStartAtNs);
    const votingEndAtNs = parseNsField(raw.voting_end_at_ns ?? raw.votingEndAtNs);
    const updatedAtNs = parseNsField(raw.updated_at_ns ?? raw.updatedAtNs);

    // org-006-4: parse `org_id` off the wire with the same defensive
    // three-state semantic used by mapOracleBridgeToProposalListItem (line 185).
    //   - field absent       → undefined → consumer treats as HWDAO (no badge,
    //                          no Org-membership check)
    //   - field === null     → null      → HWDAO-wide
    //   - field === number   → number    → Org-scoped
    // Unexpected shapes collapse to undefined so the UI degrades to "no
    // org-membership gate" rather than crashing.
    const orgIdRaw = (raw as Record<string, unknown>).org_id;
    let orgId: number | null | undefined;
    if (orgIdRaw === undefined) {
      orgId = undefined;
    } else if (orgIdRaw === null) {
      orgId = null;
    } else if (typeof orgIdRaw === 'number' && Number.isFinite(orgIdRaw)) {
      orgId = orgIdRaw;
    } else {
      orgId = undefined;
    }

    return {
      id: String(raw.id ?? proposalId),
      title: String(raw.title ?? ''),
      description: String(raw.description ?? ''),
      proposer: String(raw.proposer ?? ''),
      status: toCanisterStatus(raw.status),
      createdAt: parseIsoToMs(raw.created_at),
      votingStartAt: parseIsoToMs(raw.voting_start_at),
      votingEndAt: parseIsoToMs(raw.voting_end_at),
      ...(versionNumber !== undefined ? { versionNumber } : {}),
      hasVersions,
      ...(createdAtNs !== undefined ? { createdAtNs } : {}),
      ...(votingStartAtNs !== undefined ? { votingStartAtNs } : {}),
      ...(votingEndAtNs !== undefined ? { votingEndAtNs } : {}),
      ...(updatedAtNs !== undefined ? { updatedAtNs } : {}),
      ...(orgId !== undefined ? { orgId } : {}),
    };
  } catch (error) {
    log('error', 'Failed to fetch proposal detail', {
      proposalId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
}

/**
 * BL-220: Fetch the previous-description history for a proposal.
 *
 * Backing route: `GET /api/governance/proposals/:id/versions` (oracle-bridge,
 * lands in PLATFORM-004.3). Returns only PREVIOUS versions, oldest first —
 * the CURRENT description lives on `ProposalDetail.description`. Empty array
 * means the proposal has never been edited.
 *
 * Until PLATFORM-004.3 ships, this endpoint will 404. Callers should treat
 * an empty array as the "no history yet" case and keep rendering normally.
 */
export async function getProposalVersions(proposalId: string): Promise<ProposalVersion[]> {
  log('info', 'Fetching proposal versions', { proposalId });

  if (isMockMode()) {
    return [];
  }

  try {
    const baseUrl = getOracleBridgeUrl();
    const url = `${baseUrl}/api/governance/proposals/${encodeURIComponent(proposalId)}/versions`;
    const response = await memberAuthenticatedFetch(url);
    if (!response.ok) {
      log('warn', 'Proposal versions fetch non-OK', { proposalId, status: response.status });
      return [];
    }

    const data = await response.json();
    const raw: unknown = data?.versions ?? data;
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map((entry) => ({
        versionNumber: Number(entry.versionNumber ?? entry.version_number ?? 0),
        content: String(entry.content ?? ''),
        // BL-222: keep the Nat64 nanosecond timestamp as a string — Number()
        // would round away precision for realistic ~1.75e18 values.
        updatedAt: String(entry.updatedAt ?? entry.updated_at ?? '0'),
        updatedBy: String(entry.updatedBy ?? entry.updated_by ?? ''),
      }));
  } catch (error) {
    log('error', 'Failed to fetch proposal versions', {
      proposalId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return [];
  }
}

export interface UpdateProposalResponse {
  success: boolean;
  versionNumber?: number;
  error?: {
    code:
      | 'VALIDATION_ERROR'
      | 'UNAUTHORIZED'
      | 'WINDOW_CLOSED'
      | 'NOT_FOUND'
      | 'NETWORK_ERROR'
      | 'SERVER_ERROR';
    message: string;
  };
}

/**
 * BL-220: Submit a new description for a Draft proposal.
 *
 * Backing route: `PATCH /api/governance/proposals/:id` (oracle-bridge, lands
 * in PLATFORM-004.3). Only the original proposer can update, and only while
 * the proposal is in `Draft` status within the 24-hour review window.
 */
export async function updateProposal(
  proposalId: string,
  newDescription: string
): Promise<UpdateProposalResponse> {
  log('info', 'Updating proposal description', { proposalId });

  const trimmed = newDescription?.trim?.() ?? '';
  if (trimmed.length === 0) {
    return {
      success: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: 'Description must not be empty.',
      },
    };
  }

  // Unicode code-point count to match the canister's chars().count() rule.
  const charCount = Array.from(newDescription).length;
  if (charCount > MAX_PROPOSAL_DESCRIPTION_LENGTH) {
    return {
      success: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: `Description exceeds maximum length of ${MAX_PROPOSAL_DESCRIPTION_LENGTH} characters.`,
      },
    };
  }

  if (isMockMode()) {
    return { success: true, versionNumber: 2 };
  }

  try {
    const baseUrl = getOracleBridgeUrl();
    const url = `${baseUrl}/api/governance/proposals/${encodeURIComponent(proposalId)}`;
    const response = await memberAuthenticatedFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: newDescription }),
    });

    if (!response.ok) {
      const status = response.status;
      let message = 'Failed to update proposal description.';
      try {
        const body = await response.json();
        if (body?.message && typeof body.message === 'string') {
          message = body.message;
        }
      } catch {
        // Response had no JSON body — stick with the default message.
      }

      let code: NonNullable<UpdateProposalResponse['error']>['code'] = 'SERVER_ERROR';
      if (status === 400 || status === 422) code = 'VALIDATION_ERROR';
      else if (status === 401 || status === 403) code = 'UNAUTHORIZED';
      else if (status === 404) code = 'NOT_FOUND';
      else if (status === 409 || status === 410) code = 'WINDOW_CLOSED';

      return { success: false, error: { code, message } };
    }

    const data = await response.json();
    const versionNumber = Number(data?.versionNumber ?? data?.version_number ?? 0) || undefined;
    return { success: true, versionNumber };
  } catch (error) {
    log('error', 'Proposal update failed', {
      proposalId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error while updating proposal. Please try again.',
      },
    };
  }
}

/**
 * Service object for convenient access
 */
export const GovernanceCanisterService = {
  submitProposal,
  getProposalStatus,
  getUserVote,
  getVoteTally,
  verifyVoteStatus,
  // Story 9-1-3 + BL-038.6: Proposal listing
  getProposals,
  // BL-220: Proposal detail + edit history + update
  getProposalDetail,
  getProposalVersions,
  updateProposal,
};

export default GovernanceCanisterService;
